import React, { useState, useEffect, useMemo } from 'react';
import { 
  MessageSquare, 
  Search, 
  Users, 
  ShieldCheck, 
  FileText, 
  Download, 
  Clock, 
  Calendar,
  Scale,
  User,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  Phone,
  Briefcase
} from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import { formatDate, formatUzPhone } from '../../utils/helpers';

export default function AdminChatMonitoringPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [search, setSearch] = useState('');
  
  // Navigation mode: 'LAWYERS', 'CLIENTS', 'ALL'
  const [viewMode, setViewMode] = useState('LAWYERS');
  const [selectedLawyerId, setSelectedLawyerId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/chat/admin/monitoring');
      if (res?.success) {
        const convList = res.conversations || [];
        setConversations(convList);
        if (convList.length > 0 && !selectedConv) {
          handleSelectConversation(convList[0]);
        }
      }
    } catch (err) {
      showToast(err.message || 'Chatlarni yuklashda xatolik', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  const handleSelectConversation = async (conv) => {
    setSelectedConv(conv);
    setMessagesLoading(true);
    try {
      const res = await api.get(`/chat/conversations/${conv.id}/messages`);
      if (res?.success) {
        setMessages(res.messages || []);
      }
    } catch (err) {
      showToast(err.message || 'Xabarlarni yuklashda xatolik', 'error');
    } finally {
      setMessagesLoading(false);
    }
  };

  // Group conversations by Lawyer
  const lawyersGrouped = useMemo(() => {
    const map = new Map();
    for (const c of conversations) {
      const lid = c.lawyer?.id || 0;
      if (!map.has(lid)) {
        map.set(lid, {
          lawyer: c.lawyer,
          conversations: [],
          totalMessages: 0,
        });
      }
      const item = map.get(lid);
      item.conversations.push(c);
      item.totalMessages += c._count?.messages || 0;
    }
    return Array.from(map.values());
  }, [conversations]);

  // Group conversations by Client
  const clientsGrouped = useMemo(() => {
    const map = new Map();
    for (const c of conversations) {
      const cid = c.client?.id || 0;
      if (!map.has(cid)) {
        map.set(cid, {
          client: c.client,
          conversations: [],
          totalMessages: 0,
        });
      }
      const item = map.get(cid);
      item.conversations.push(c);
      item.totalMessages += c._count?.messages || 0;
    }
    return Array.from(map.values());
  }, [conversations]);

  // Filtered by Search
  const filteredConversations = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter((c) => (
      c.lawyer?.fullName?.toLowerCase().includes(q) ||
      c.client?.fullName?.toLowerCase().includes(q) ||
      c.client?.phone?.includes(q) ||
      c.lastMessage?.toLowerCase().includes(q) ||
      c.subject?.toLowerCase().includes(q)
    ));
  }, [conversations, search]);

  const filteredLawyers = useMemo(() => {
    if (!search.trim()) return lawyersGrouped;
    const q = search.toLowerCase();
    return lawyersGrouped.filter((g) => (
      g.lawyer?.fullName?.toLowerCase().includes(q) ||
      g.lawyer?.position?.toLowerCase().includes(q) ||
      g.conversations.some(c => c.client?.fullName?.toLowerCase().includes(q) || c.client?.phone?.includes(q))
    ));
  }, [lawyersGrouped, search]);

  const filteredClients = useMemo(() => {
    if (!search.trim()) return clientsGrouped;
    const q = search.toLowerCase();
    return clientsGrouped.filter((g) => (
      g.client?.fullName?.toLowerCase().includes(q) ||
      g.client?.phone?.includes(q) ||
      g.conversations.some(c => c.lawyer?.fullName?.toLowerCase().includes(q))
    ));
  }, [clientsGrouped, search]);

  return (
    <div className="flex flex-col h-[calc(100vh-125px)] min-h-[500px] space-y-3 overflow-hidden">
      
      {/* Top Banner */}
      <div className="shrink-0 bg-[#090e18] border border-[#c5a880]/30 rounded-xl p-3 sm:p-3.5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-[#142034] border border-[#c5a880]/40 flex items-center justify-center text-[#c5a880] shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-serif text-base font-bold text-slate-100 flex items-center gap-2">
              <span>Yozishmalar Nazorati (Audit & Monitoring)</span>
              <span className="px-2 py-0.5 rounded text-[10px] bg-[#142034] text-[#c5a880] border border-[#c5a880]/30 font-sans">
                Real Vaqt
              </span>
            </h3>
            <span className="text-xs text-slate-400">
              Advokatlar va mijozlar shaxsiy chatlari va yuborilgan hujjatlar to‘liq arxivi
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={loadConversations}
          className="px-3.5 py-1.5 rounded-lg bg-[#142034] hover:bg-[#1b2b46] border border-slate-700 text-xs font-semibold text-slate-200 flex items-center transition-colors cursor-pointer shrink-0"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Yangilash
        </button>
      </div>

      {/* Main 2-Column Grid - Viewport Fitted */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 overflow-hidden">
        
        {/* Left Column: Filter Tabs & Grouped Lists */}
        <div className="lg:col-span-5 bg-white rounded-xl border border-slate-200 shadow-2xs flex flex-col h-full min-h-0 overflow-hidden">
          
          {/* Top Category Tabs (Advokatlar / Mijozlar / Barcha) */}
          <div className="p-2 border-b border-slate-200 bg-slate-50 flex items-center gap-1.5 overflow-x-auto">
            <button
              type="button"
              onClick={() => { setViewMode('LAWYERS'); setSelectedLawyerId(null); }}
              className={`flex-1 py-1.5 px-2.5 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center justify-center gap-1.5 whitespace-nowrap ${
                viewMode === 'LAWYERS'
                  ? 'bg-[#091322] text-[#c5a880] shadow-xs'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <Scale className="w-3.5 h-3.5" />
              <span>Advokatlar ({lawyersGrouped.length})</span>
            </button>

            <button
              type="button"
              onClick={() => { setViewMode('CLIENTS'); setSelectedClientId(null); }}
              className={`flex-1 py-1.5 px-2.5 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center justify-center gap-1.5 whitespace-nowrap ${
                viewMode === 'CLIENTS'
                  ? 'bg-[#091322] text-[#c5a880] shadow-xs'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              <span>Mijozlar ({clientsGrouped.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setViewMode('ALL')}
              className={`py-1.5 px-2.5 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center justify-center gap-1 whitespace-nowrap ${
                viewMode === 'ALL'
                  ? 'bg-[#091322] text-[#c5a880] shadow-xs'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Barchasi</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-2.5 border-b border-slate-100">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={
                  viewMode === 'LAWYERS' 
                    ? 'Advokat yoki mijoz ismini izlash...'
                    : viewMode === 'CLIENTS'
                    ? 'Mijoz ismi yoki telefon raqamini izlash...'
                    : 'Yozishmalardan izlash...'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#c5a880]"
              />
            </div>
          </div>

          {/* List Content based on viewMode */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <div className="py-16 text-center text-xs text-slate-400">Yuklanmoqda...</div>
            ) : viewMode === 'LAWYERS' ? (
              filteredLawyers.length === 0 ? (
                <div className="py-16 text-center text-xs text-slate-400">Advokatlar topilmadi</div>
              ) : (
                filteredLawyers.map((g) => {
                  const isExpanded = selectedLawyerId === g.lawyer?.id;
                  return (
                    <div key={g.lawyer?.id || Math.random()} className="border-b border-slate-100 last:border-b-0">
                      {/* Lawyer Header Item */}
                      <div
                        onClick={() => setSelectedLawyerId(isExpanded ? null : g.lawyer?.id)}
                        className={`p-3 transition-colors cursor-pointer flex items-center justify-between ${
                          isExpanded ? 'bg-purple-50/60' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="min-w-0 flex items-center space-x-2.5">
                          <div className="w-8 h-8 rounded-full bg-purple-100 border border-purple-300 flex items-center justify-center text-purple-700 shrink-0 font-bold text-xs">
                            ⚖️
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-xs text-slate-900 truncate">
                              {g.lawyer?.fullName || 'Noma’lum Advokat'}
                            </div>
                            <span className="text-[10px] text-purple-700 font-semibold block">
                              {g.lawyer?.position || 'Advokat'} • {g.conversations.length} ta mijoz bilan yozishma
                            </span>
                          </div>
                        </div>

                        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90 text-purple-700' : ''}`} />
                      </div>

                      {/* Expanded Sub-list of Clients who chatted with this Lawyer */}
                      {isExpanded && (
                        <div className="bg-slate-50/80 border-t border-slate-200 divide-y divide-slate-100 pl-4">
                          {g.conversations.map((c) => {
                            const isSelected = selectedConv?.id === c.id;
                            return (
                              <div
                                key={c.id}
                                onClick={() => handleSelectConversation(c)}
                                className={`p-2.5 transition-colors cursor-pointer ${
                                  isSelected 
                                    ? 'bg-amber-100/70 border-l-4 border-[#c5a880]' 
                                    : 'hover:bg-white'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-[11.5px] text-slate-900 truncate">
                                    👤 {c.client?.fullName || 'Mijoz'}
                                  </span>
                                  <span className="text-[9.5px] font-mono text-slate-400">
                                    {c._count?.messages || 0} ta xabar
                                  </span>
                                </div>
                                <div className="text-[10.5px] text-slate-500 font-mono mt-0.5">
                                  {c.client?.phone ? formatUzPhone(c.client.phone) : 'Tel yo‘q'}
                                </div>
                                <p className="text-[10.5px] text-slate-600 truncate mt-1 italic">
                                  "{c.lastMessage || 'Xabar mavjud'}"
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )
            ) : viewMode === 'CLIENTS' ? (
              filteredClients.length === 0 ? (
                <div className="py-16 text-center text-xs text-slate-400">Mijozlar topilmadi</div>
              ) : (
                filteredClients.map((g) => {
                  const isExpanded = selectedClientId === g.client?.id;
                  return (
                    <div key={g.client?.id || Math.random()} className="border-b border-slate-100 last:border-b-0">
                      {/* Client Header Item */}
                      <div
                        onClick={() => setSelectedClientId(isExpanded ? null : g.client?.id)}
                        className={`p-3 transition-colors cursor-pointer flex items-center justify-between ${
                          isExpanded ? 'bg-blue-50/60' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="min-w-0 flex items-center space-x-2.5">
                          <div className="w-8 h-8 rounded-full bg-blue-100 border border-blue-300 flex items-center justify-center text-blue-700 shrink-0 font-bold text-xs">
                            👤
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-xs text-slate-900 truncate">
                              {g.client?.fullName || 'Mijoz'}
                            </div>
                            <span className="text-[10px] text-blue-700 font-mono block">
                              {g.client?.phone ? formatUzPhone(g.client.phone) : 'Tel ko‘rsatilmagan'} • {g.conversations.length} ta advokat
                            </span>
                          </div>
                        </div>

                        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90 text-blue-700' : ''}`} />
                      </div>

                      {/* Expanded Sub-list of Lawyers this Client chatted with */}
                      {isExpanded && (
                        <div className="bg-slate-50/80 border-t border-slate-200 divide-y divide-slate-100 pl-4">
                          {g.conversations.map((c) => {
                            const isSelected = selectedConv?.id === c.id;
                            return (
                              <div
                                key={c.id}
                                onClick={() => handleSelectConversation(c)}
                                className={`p-2.5 transition-colors cursor-pointer ${
                                  isSelected 
                                    ? 'bg-amber-100/70 border-l-4 border-[#c5a880]' 
                                    : 'hover:bg-white'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-[11.5px] text-purple-900 truncate">
                                    ⚖️ {c.lawyer?.fullName || 'Advokat'}
                                  </span>
                                  <span className="text-[9.5px] font-mono text-slate-400">
                                    {c._count?.messages || 0} ta xabar
                                  </span>
                                </div>
                                <p className="text-[10.5px] text-slate-600 truncate mt-1 italic">
                                  "{c.lastMessage || 'Xabar mavjud'}"
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )
            ) : (
              // ALL VIEW
              filteredConversations.length === 0 ? (
                <div className="py-16 text-center text-xs text-slate-400">Yozishmalar topilmadi</div>
              ) : (
                filteredConversations.map((c) => {
                  const isSelected = selectedConv?.id === c.id;
                  return (
                    <div
                      key={c.id}
                      onClick={() => handleSelectConversation(c)}
                      className={`p-3 transition-colors cursor-pointer ${
                        isSelected
                          ? 'bg-amber-50/70 border-l-4 border-[#c5a880]'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Distinct Separated Lawyer vs Client Badges */}
                      <div className="space-y-1 mb-1.5">
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200 truncate max-w-[220px]">
                            ⚖️ Advokat: {c.lawyer?.fullName}
                          </span>
                          <span className="text-[9.5px] text-slate-400 font-mono">
                            {formatDate(c.lastMessageAt || c.updatedAt)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200 truncate max-w-[220px]">
                            👤 Mijoz: {c.client?.fullName} ({c.client?.phone ? formatUzPhone(c.client.phone) : 'Tel yo‘q'})
                          </span>
                          <span className="text-[9.5px] text-slate-400 font-mono">
                            {c._count?.messages || 0} ta xabar
                          </span>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-600 truncate mt-1">
                        {c.lastMessage || 'Xabar mavjud'}
                      </p>
                    </div>
                  );
                })
              )
            )}
          </div>
        </div>

        {/* Right Column: Live Chat Transcript */}
        <div className="lg:col-span-7 bg-[#070c16] rounded-xl border border-slate-800 shadow-2xs flex flex-col h-full min-h-0 overflow-hidden text-slate-200">
          {selectedConv ? (
            <>
              {/* Detailed Header with DISTINCT lawyer vs client rows */}
              <div className="p-3.5 border-b border-[#162238] bg-[#090e18]">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-purple-950/80 border border-purple-700/60 text-purple-300">
                        ⚖️ Advokat: {selectedConv.lawyer?.fullName}
                      </span>
                      <span className="text-[10.5px] text-slate-400">
                        {selectedConv.lawyer?.position} • {selectedConv.lawyer?.phone}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-950/80 border border-blue-700/60 text-blue-300">
                        👤 Mijoz: {selectedConv.client?.fullName}
                      </span>
                      <span className="text-[10.5px] text-slate-400 font-mono">
                        Tel: {selectedConv.client?.phone ? formatUzPhone(selectedConv.client.phone) : '—'}
                      </span>
                    </div>

                    <div className="text-[10px] text-[#c5a880] pt-0.5">
                      Mavzu: {selectedConv.subject || 'Dastlabki yuridik konsultatsiya'}
                    </div>
                  </div>

                  <div className="px-2 py-1 rounded bg-[#101b30] border border-[#c5a880]/30 text-[#c5a880] text-[10px] font-bold shrink-0">
                    🛡️ Audit Rejimi
                  </div>
                </div>
              </div>

              {/* Message History */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-[#050912]">
                {messagesLoading ? (
                  <div className="py-16 text-center text-xs text-slate-400">Xabarlar yuklanmoqda...</div>
                ) : messages.length === 0 ? (
                  <div className="py-16 text-center text-xs text-slate-400">Ushbu chatda xabarlar yo‘q</div>
                ) : (
                  messages.map((m) => {
                    const isLawyer = m.senderType === 'LAWYER';

                    return (
                      <div
                        key={m.id}
                        className={`flex flex-col ${isLawyer ? 'items-start' : 'items-end'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl p-3 shadow-md ${
                            isLawyer
                              ? 'bg-[#0f192b] border border-[#1e3153] text-slate-200 rounded-tl-xs'
                              : 'bg-[#18263e] border border-[#2a4066] text-slate-100 rounded-tr-xs'
                          }`}
                        >
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`text-[10.5px] font-bold ${isLawyer ? 'text-purple-300' : 'text-blue-300'}`}>
                              {isLawyer ? `⚖️ Advokat: ${m.senderName}` : `👤 Mijoz: ${m.senderName}`}
                            </span>
                          </div>

                          {/* Attached Document or Image */}
                          {m.fileUrl && (
                            <div className="mb-2">
                              {m.fileType === 'IMAGE' ? (
                                <a href={m.fileUrl} target="_blank" rel="noopener noreferrer">
                                  <img src={m.fileUrl} alt="Hujjat" className="rounded-lg max-h-56 object-cover border border-slate-700" />
                                </a>
                              ) : (
                                <a
                                  href={m.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-2 p-2 rounded-lg bg-[#070b14] border border-[#1e2f4c] text-xs text-slate-200 hover:border-[#c5a880] transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-red-400 shrink-0" />
                                  <span className="truncate">{m.fileName || 'PDF Hujjat'}</span>
                                  <Download className="w-3.5 h-3.5 text-slate-400 ml-auto shrink-0" />
                                </a>
                              )}
                            </div>
                          )}

                          {m.text && (
                            <p className="text-xs leading-relaxed whitespace-pre-line break-words">
                              {m.text}
                            </p>
                          )}

                          <div className="text-[9px] text-slate-500 text-right mt-1 font-mono">
                            {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, {formatDate(m.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-slate-500 text-center">
              <MessageSquare className="w-12 h-12 mb-2 opacity-30 text-[#c5a880]" />
              <p className="text-xs">Yozishmalar tarixini ko‘rish uchun chapdagi ro‘yxatdan advokat yoki mijozni tanlang</p>
            </div>
          )}
        </div>

      </div>

    </div>
  );
}
